import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Dropdown,
  Input,
  Stack
} from "@adfenix/alexandria";
import InfoCircle from "static/svg/info-circle";
import { Controller, useFormContext } from "react-hook-form";
import { Modal } from "components/Modal";
import { useState } from "react";
import Popover from "components/Popover";
import { ProgramService } from "@adfenix/types";
import { useDisabledFields } from "../useDisabledFields";
import { useTranslation } from "react-i18next";

export const EditBundleGroup = ({
  bundleGroupIndex,
  onSave,
  onCancel,
  isNew
}: {
  bundleGroupIndex: number;
  isNew: boolean;
  onCancel: () => void;
  onSave: () => void;
}) => {
  const { t } = useTranslation();

  const disabledFields = useDisabledFields();
  const { watch, register, control, setValue } =
    useFormContext<ProgramService.ProgramModel>();

  const { bundleGroups } = watch();

  const [sortOrder, setSortOrder] = useState(
    bundleGroups?.[bundleGroupIndex].sortOrder ?? 1
  );

  const save = () => {
    setValue(`bundleGroups.${bundleGroupIndex}.sortOrder`, sortOrder, {
      shouldDirty: true
    });
    onSave();
  };

  return (
    <Modal.Card>
      <Modal.Card.Head onClose={onCancel}>
        <Modal.Card.Title>
          {isNew
            ? t("programBuilder.program.details.bundleGroup.header.add")
            : t("programBuilder.program.details.bundleGroup.header.edit")}
        </Modal.Card.Title>
      </Modal.Card.Head>

      <Modal.Card.Body>
        <Stack spacing="m" pY="gutter">
          <Input
            label={t(
              "programBuilder.program.details.bundleGroup.input.name.label"
            )}
            {...register(`bundleGroups.${bundleGroupIndex}.name`)}
            readOnly={disabledFields("bundleGroups.name")}
          />

          <Controller
            control={control}
            name={`bundleGroups.${bundleGroupIndex}.legacyType`}
            render={({ field }) => (
              <Box>
                <Box display="flex">
                  <div>
                    {t(
                      "programBuilder.program.details.bundleGroup.legacyProduct"
                    )}
                  </div>
                  <Popover
                    style={{
                      display: "flex",
                      width: "23px",
                      justifyContent: "center",
                      marginLeft: "4px",
                      marginBottom: "4px"
                    }}
                    trigger={<InfoCircle />}
                  >
                    {t(
                      "programBuilder.program.details.bundleGroup.legacyProduct.popover"
                    )}
                  </Popover>
                </Box>

                <Dropdown
                  items={[
                    "HomeBooster",
                    "PreSaleAgentBooster",
                    "PostSaleAgentBooster",
                    "Reboost",
                    "Upcoming",
                    "DisplayAd"
                  ]}
                  {...field}
                  disabled={disabledFields("bundleGroups.legacyType")}
                />
              </Box>
            )}
          />

          <Box>
            <Checkbox
              label={t(
                "programBuilder.program.details.bundleGroup.checkbox.default"
              )}
              {...register(`bundleGroups.${bundleGroupIndex}.default`)}
              readOnly={disabledFields("bundleGroups.default")}
            />
          </Box>

          <Box>
            <Checkbox
              label={t(
                "programBuilder.program.details.bundleGroup.checkbox.configurable"
              )}
              {...register(`bundleGroups.${bundleGroupIndex}.configurable`)}
              readOnly={disabledFields("bundleGroups.configurable")}
            />
          </Box>

          <Box style={{ width: "50%" }}>
            <Input
              type="number"
              label={t("programBuilder.program.details.bundleGroup.sortOrder")}
              value={sortOrder}
              min={0}
              onChange={e => setSortOrder(Number(e.target.value))}
              readOnly={disabledFields("bundleGroups.sortOrder")}
            />
          </Box>
        </Stack>
      </Modal.Card.Body>

      <Modal.Card.Foot style={{ alignItems: "normal" }}>
        <ButtonGroup>
          <Button
            logLabel="bundle-group-edit-cancel"
            onClick={onCancel}
            variant="secondary"
          >
            {t("programBuilder.program.details.bundleGroup.button.cancel")}
          </Button>
          <Button
            logLabel="bundle-group-edit"
            onClick={save}
            variant="primary"
            data-testid="bundle-group-edit-primary"
          >
            {!isNew
              ? t("programBuilder.program.details.bundleGroup.button.add")
              : t("programBuilder.program.details.bundleGroup.button.edit")}
          </Button>
        </ButtonGroup>
      </Modal.Card.Foot>
    </Modal.Card>
  );
};
